import { useMemo } from 'react';
import { GuidT, SelectIF } from '@/types/common';
import { propertyApi } from '@/api/propertyApi/propertyApi';
import { getFitPropertiesByWardrobeCategoryType } from '@/helpers/fitProperties';
import { createWardrobeCategories } from '@/helpers/wardrobeCategories';
import {
    FIT_ATTRIBUTE_TYPE,
    LOWER_FIT_ATTRIBUTE_TYPE,
} from '@/constants/attributeTypes';
import { EVENTS_PROPERTIES } from '@/constants/events';
import { GENDERS_PROPERTIES } from '@/constants/genders';
import { PATTERNS_PROPERTIES } from '@/constants/patterns';
import { SEASONS_PROPERTIES } from '@/constants/seasons';
import { SIZES_PROPERTIES } from '@/constants/sizes';

interface CategoryFitConfigIF {
    properties: SelectIF[];
    attributeGuid: GuidT;
}

export interface FitConfigByCategoryIF {
    [categoryGuid: GuidT]: CategoryFitConfigIF;
}

export interface ClothingPropertiesIF {
    categoriesProperties: SelectIF<GuidT>[] | null | undefined;
    colorsProperties: SelectIF<GuidT>[] | null | undefined;
    brandsProperties: SelectIF<GuidT>[] | null | undefined;
    materialsProperties: SelectIF<GuidT>[] | null | undefined;
    eventsProperties: typeof EVENTS_PROPERTIES;
    fitConfigByCategory: FitConfigByCategoryIF;
    gendersProperties: typeof GENDERS_PROPERTIES;
    patternsProperties: typeof PATTERNS_PROPERTIES;
    seasonsProperties: typeof SEASONS_PROPERTIES;
    sizesProperties: typeof SIZES_PROPERTIES;
    isLoading: boolean;
}

const extractFitsFromCategories = (
    categories: SelectIF<GuidT>[] | null | undefined,
): FitConfigByCategoryIF => {
    const fitConfigByCategory: FitConfigByCategoryIF = {};

    if (!categories?.length) {
        return fitConfigByCategory;
    }

    const wardrobeCategories = createWardrobeCategories(categories);

    wardrobeCategories.forEach((wardrobeCategory) => {
        if (!wardrobeCategory.categoryGuid) {
            return;
        }

        const properties = getFitPropertiesByWardrobeCategoryType(
            wardrobeCategory.type,
        );

        if (!properties?.length) {
            return;
        }

        const parentCategory = categories.find(
            (category) => category.value === wardrobeCategory.categoryGuid,
        );

        const fitAttribute = parentCategory?.settings?.attributes?.find(
            (attribute) =>
                attribute.type === FIT_ATTRIBUTE_TYPE ||
                attribute.type === LOWER_FIT_ATTRIBUTE_TYPE,
        );

        if (!fitAttribute) {
            return;
        }

        const fitConfig = {
            properties,
            attributeGuid: fitAttribute.guid,
        };

        fitConfigByCategory[wardrobeCategory.categoryGuid] = fitConfig;

        categories.forEach((category) => {
            if (category.settings?.parentId !== wardrobeCategory.categoryGuid) {
                return;
            }

            fitConfigByCategory[category.value] = fitConfig;
        });
    });

    return fitConfigByCategory;
};

const STATIC_PROPERTIES = {
    eventsProperties: EVENTS_PROPERTIES,
    gendersProperties: GENDERS_PROPERTIES,
    patternsProperties: PATTERNS_PROPERTIES,
    seasonsProperties: SEASONS_PROPERTIES,
    sizesProperties: SIZES_PROPERTIES,
};

export const useClothingProperties = (): ClothingPropertiesIF => {
    const [categories, colors, brands, materials] = [
        propertyApi.useGetCategoriesSelectPropertiesQuery(),
        propertyApi.useGetColorsSelectPropertiesQuery(),
        propertyApi.useGetBrandsSelectPropertiesQuery(),
        propertyApi.useGetMaterialsSelectPropertiesQuery(),
    ];

    const fitConfigByCategory = useMemo(
        () => extractFitsFromCategories(categories.data),
        [categories.data],
    );

    return {
        categoriesProperties: categories.data,
        colorsProperties: colors.data,
        brandsProperties: brands.data,
        materialsProperties: materials.data,
        fitConfigByCategory,
        ...STATIC_PROPERTIES,
        isLoading:
            categories.isLoading ||
            colors.isLoading ||
            brands.isLoading ||
            materials.isLoading,
    };
};
